import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { ArrowLeft, Plus, PencilSimple, Buildings, ShieldCheck } from '@phosphor-icons/react';

export default function InstitutionsManagement() {
  const { authAxios } = useAuth();
  const navigate = useNavigate();
  const [institutions, setInstitutions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', code: '', description: '' });
  const [saving, setSaving] = useState(false);

  const fetchInstitutions = () => {
    authAxios().get('/institutions/all')
      .then(res => setInstitutions(res.data))
      .catch(() => toast.error('Kurumlar yüklenemedi'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchInstitutions(); }, [authAxios]); // eslint-disable-line

  const openCreate = () => {
    setEditing(null);
    setForm({ name: '', code: '', description: '' });
    setDialogOpen(true);
  };

  const openEdit = (inst) => {
    setEditing(inst);
    setForm({ name: inst.name, code: inst.code, description: inst.description || '' });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name || !form.code) { toast.error('Ad ve kod zorunlu'); return; }
    setSaving(true);
    try {
      if (editing) {
        await authAxios().put(`/institutions/${editing.id}`, form);
        toast.success('Kurum güncellendi');
      } else {
        await authAxios().post('/institutions', form);
        toast.success('Kurum oluşturuldu');
      }
      setDialogOpen(false);
      fetchInstitutions();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'İşlem başarısız');
    } finally { setSaving(false); }
  };

  const toggleActive = async (inst) => {
    try {
      await authAxios().put(`/institutions/${inst.id}`, { is_active: !inst.is_active });
      toast.success(inst.is_active ? 'Kurum devre disi birakildi' : 'Kurum etkinlestirildi');
      fetchInstitutions();
    } catch { toast.error('İşlem başarısız'); }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <header className="bg-[#1C1C1E] border-b border-[#2C2C2E] px-4 py-3 flex items-center gap-3">
        <Button data-testid="back-button" variant="ghost" onClick={() => navigate('/admin')} className="text-white hover:bg-[#2C2C2E] h-10 w-10 p-0">
          <ArrowLeft weight="bold" className="w-5 h-5" />
        </Button>
        <div className="flex items-center gap-2">
          <Buildings weight="fill" className="w-5 h-5 text-[hsl(var(--primary))]" />
          <h1 className="font-heading text-lg text-white">KURUM YÖNETİMİ</h1>
        </div>
        <Button data-testid="create-institution-button" onClick={openCreate} className="ml-auto bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-white h-10 font-heading uppercase text-xs">
          <Plus weight="bold" className="w-4 h-4 mr-1" /> Yeni Kurum
        </Button>
      </header>

      <main className="p-4 max-w-3xl mx-auto">
        {loading ? (
          <div className="text-center py-12 text-[hsl(var(--muted-foreground))]">Yükleniyor...</div>
        ) : (
          <div className="space-y-3">
            {institutions.map(inst => (
              <Card key={inst.id} className="bg-[#1C1C1E] border-[#2C2C2E]">
                <CardContent className="p-4 flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-heading text-white">{inst.name}</h3>
                      <Badge variant="outline" className="text-xs border-[#2C2C2E] text-[hsl(var(--muted-foreground))]">{inst.code}</Badge>
                      <Badge className={inst.is_active ? 'bg-[hsl(var(--success))] text-white text-xs' : 'bg-[#2C2C2E] text-[#666] text-xs'}>
                        {inst.is_active ? 'Aktif' : 'Pasif'}
                      </Badge>
                    </div>
                    {inst.description && <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">{inst.description}</p>}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      data-testid={`edit-inst-${inst.id}`}
                      variant="ghost"
                      onClick={() => openEdit(inst)}
                      className="text-[hsl(var(--muted-foreground))] hover:text-white hover:bg-[#2C2C2E] h-9 w-9 p-0"
                    >
                      <PencilSimple weight="bold" className="w-4 h-4" />
                    </Button>
                    <Button
                      data-testid={`toggle-inst-${inst.id}`}
                      variant="ghost"
                      onClick={() => toggleActive(inst)}
                      className="text-[hsl(var(--muted-foreground))] hover:text-white hover:bg-[#2C2C2E] h-9 text-xs"
                    >
                      {inst.is_active ? 'Devre Dışı' : 'Etkinleştir'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#1C1C1E] border-[#2C2C2E] text-white">
          <DialogHeader>
            <DialogTitle className="font-heading text-white">{editing ? 'KURUM DÜZENLE' : 'YENİ KURUM'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="overline text-[hsl(var(--muted-foreground))] mb-1.5 block">Kurum Adı</label>
              <Input data-testid="institution-name-input" value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="h-12 bg-[#0A0A0A] border-[#2C2C2E] text-white" />
            </div>
            <div>
              <label className="overline text-[hsl(var(--muted-foreground))] mb-1.5 block">Kurum Kodu</label>
              <Input data-testid="institution-code-input" value={form.code} onChange={e => setForm({...form, code: e.target.value})} className="h-12 bg-[#0A0A0A] border-[#2C2C2E] text-white" />
            </div>
            <div>
              <label className="overline text-[hsl(var(--muted-foreground))] mb-1.5 block">Açıklama</label>
              <Input data-testid="institution-desc-input" value={form.description} onChange={e => setForm({...form, description: e.target.value})} className="h-12 bg-[#0A0A0A] border-[#2C2C2E] text-white" />
            </div>
          </div>
          <DialogFooter>
            <Button data-testid="save-institution-button" onClick={handleSave} disabled={saving} className="bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-white h-12 font-heading uppercase">
              {saving ? 'Kaydediliyor...' : 'Kaydet'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
